import { useState, useEffect } from 'react';
import { Copy, Receipt, Plus } from 'lucide-react';
import { colors } from '../styles/colors';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useYear } from '../contexts/YearContext';
import PageHeader from '../components/PageHeader';
import FilterBar from '../components/FilterBar';
import AnimatedContent from '../components/AnimatedContent';
import ChargesSummaryCards from '../components/ChargesSummaryCards';
import ChargesTable from '../components/ChargesTable';
import ChargesConsultantTable from '../components/charges/ChargesConsultantTable';
import ChargesTablesGrid from '../components/charges/ChargesTablesGrid';
import ConsultantConfigButton from '../components/charges/ConsultantConfigButton';
import ChargeFormModal from '../components/charges/ChargeFormModal';
import DeleteChargeModal from '../components/charges/DeleteChargeModal';
import CopyChargesModal from '../components/charges/CopyChargesModal';
import ConsultantConfigModal from '../components/charges/ConsultantConfigModal';
import {
  getCharges as fetchCharges,
  getConsultantConfig as fetchConsultantConfig,
  getConsultants as fetchConsultants,
  getTemps,
  createCharge,
  updateCharge,
  deleteCharge,
  copyCharges,
  updateConsultantConfig
} from '../services/airtable';
import { getAvailableYearsFromTemps } from '../utils/yearUtils';

function ChargesPage() {
  const { showToast } = useToast();
  const { hasPermission, ACTIONS, RESOURCES } = useAuth();
  const { selectedYear, setSelectedYear, availableYears, setAvailableYears } = useYear();

  // Vérifier si l'utilisateur peut modifier les charges
  const canEditCharges = hasPermission(ACTIONS.WRITE, RESOURCES.CHARGES);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', or 'edit-group'
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [chargeToDelete, setChargeToDelete] = useState(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedConsultantIds, setSelectedConsultantIds] = useState([]);
  const nombreConsultants = selectedConsultantIds.length;
  const [consultantsList, setConsultantsList] = useState([]);
  const [consultantsMap, setConsultantsMap] = useState({});

  // Filtres
  const [initialLoad, setInitialLoad] = useState(true);

  // Chargement initial
  useEffect(() => {
    loadChargesInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chargement sur changement d'année (après le chargement initial)
  useEffect(() => {
    if (!initialLoad && selectedYear !== null) {
      loadCharges();
      loadConsultantConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // Recharger après reconnexion (session expirée → renouvelée)
  useEffect(() => {
    const handler = () => loadChargesInitial();
    window.addEventListener('session:renewed', handler);
    return () => window.removeEventListener('session:renewed', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadChargesInitial = async () => {
    try {
      setLoading(true);
      const [data, tempsData] = await Promise.all([
        fetchCharges(),
        getTemps()
      ]);

      // Définir les années disponibles depuis les temps
      setAvailableYears(getAvailableYearsFromTemps(tempsData));

      // Filtrer par année sélectionnée
      const filteredData = data.filter(c => c.annee === selectedYear);
      setCharges(filteredData);

      // Charger la config consultants
      try {
        const allConfigs = await fetchConsultantConfig();
        const yearConfig = Array.isArray(allConfigs)
          ? allConfigs.find(c => c.annee === selectedYear)
          : null;
        setSelectedConsultantIds(yearConfig?.consultantIds || []);
      } catch (err) {
        setSelectedConsultantIds([]);
      }

      // Charger la liste des consultants
      try {
        const consultantsData = await fetchConsultants();
        const list = consultantsData.map(c => ({
          id: c.id,
          nom: c.fields?.Consultant || c.fields?.Nom || c.id
        }));
        setConsultantsList(list);
        const map = {};
        list.forEach(c => { map[c.id] = c.nom; });
        setConsultantsMap(map);
      } catch (err) {
        setConsultantsList([]);
        setConsultantsMap({});
      }

      setInitialLoad(false);
    } catch (err) {
      showToast(err.message || 'Erreur lors du chargement des charges', 'error');
      setInitialLoad(false);
    } finally {
      setLoading(false);
    }
  };

  const loadCharges = async () => {
    try {
      setLoading(true);
      const data = await fetchCharges();

      // Filtrer par année
      const filteredData = data.filter(c => c.annee === selectedYear);
      setCharges(filteredData);
    } catch (err) {
      showToast(err.message || 'Erreur lors du chargement des charges', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadConsultantConfig = async () => {
    if (!selectedYear) return;
    try {
      const allConfigs = await fetchConsultantConfig();
      const yearConfig = Array.isArray(allConfigs)
        ? allConfigs.find(c => c.annee === selectedYear)
        : null;
      setSelectedConsultantIds(yearConfig?.consultantIds || []);
    } catch (err) {
      setSelectedConsultantIds([]);
    }
  };

  const handleOpenModal = (mode, charge = null) => {
    setModalMode(mode);
    setSelectedCharge(charge);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCharge(null);
    setModalMode('create');
  };

  const handleSubmitCharge = async (formData) => {
    try {
      // Créer le payload selon le mode
      const payload = {
        nom: formData.nom,
        type: formData.type,
        annee: formData.annee,
        montant: parseFloat(formData.montant)
      };

      // Ajouter mois ou trimestre selon le type
      if (formData.type === 'Trimestrielle' && formData.trimestre) {
        payload.trimestre = formData.trimestre;
      } else if ((formData.type === 'Mensuelle' || formData.type === 'Exceptionnelle') && formData.mois) {
        payload.mois = parseInt(formData.mois);
      }

      // Ajouter consultant si spécifié
      if (formData.consultant) {
        payload.consultant = formData.consultant;
      }

      if (modalMode === 'create') {
        // Gestion création multiple si tousLesMois ou tousLesTrimestres
        if (formData.tousLesMois) {
          const promises = [];
          for (let mois = 1; mois <= 12; mois++) {
            promises.push(createCharge({ ...payload, mois }));
          }
          await Promise.all(promises);
        } else if (formData.tousLesTrimestres) {
          const promises = ['Q1', 'Q2', 'Q3', 'Q4'].map(trimestre =>
            createCharge({ ...payload, trimestre })
          );
          await Promise.all(promises);
        } else {
          await createCharge(payload);
        }
      } else if (modalMode === 'edit-group') {
        // Modifier toutes les charges du groupe
        const groupPayload = {
          nom: formData.nom,
          montant: parseFloat(formData.montant)
        };
        if (formData.consultant) {
          groupPayload.consultant = formData.consultant;
        }
        const promises = selectedCharge.charges.map(charge =>
          updateCharge(charge.id, groupPayload)
        );
        await Promise.all(promises);
      } else {
        // Modifier une seule charge
        await updateCharge(selectedCharge.id, payload);
      }

      showToast(
        modalMode === 'create' ? 'Charge créée avec succès' : 'Charge modifiée avec succès',
        'success'
      );
      handleCloseModal();
      loadCharges();
    } catch (err) {
      throw err; // Re-throw pour que le modal gère l'erreur
    }
  };

  const handleDeleteClick = (charge) => {
    setChargeToDelete(charge);
    setShowDeleteModal(true);
  };

  const handleDeleteGroup = (group) => {
    setChargeToDelete(group);
    setShowDeleteModal(true);
  };

  const handleEditGroup = (group) => {
    setSelectedCharge(group);
    setModalMode('edit-group');
    setShowModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (chargeToDelete.charges) {
        // Supprimer toutes les charges du groupe
        const deletePromises = chargeToDelete.charges.map(charge =>
          deleteCharge(charge.id)
        );
        await Promise.all(deletePromises);
        showToast(`${chargeToDelete.charges.length} charge(s) supprimée(s) avec succès`, 'success');
      } else {
        // Supprimer une seule charge
        await deleteCharge(chargeToDelete.id);
        showToast('Charge supprimée avec succès', 'success');
      }

      setShowDeleteModal(false);
      setChargeToDelete(null);
      loadCharges();
    } catch (err) {
      showToast(err.message || 'Erreur lors de la suppression', 'error');
    }
  };

  const handleCopyCharges = async (fromYear, toYear) => {
    try {
      const data = await copyCharges(fromYear, toYear);
      showToast(data.message, 'success');
      setShowCopyModal(false);
      setSelectedYear(parseInt(toYear));
      loadCharges();
    } catch (err) {
      // Si la session a expiré, la modale de reconnexion gère la suite ;
      // sinon on affiche l'erreur (showToast est filtré par isSessionExpired()).
      showToast(err.message || 'Erreur lors de la copie des charges', 'error');
    }
  };

  const handleSaveConsultantConfig = async (annee, consultantIds) => {
    try {
      await updateConsultantConfig(annee, consultantIds);
      setSelectedConsultantIds(consultantIds);
      showToast('Configuration enregistrée avec succès', 'success');
    } catch (err) {
      throw err; // Re-throw pour que le modal gère l'erreur
    }
  };

  // Grouper les charges communes par type (sans consultant)
  const chargesByType = {
    'Trimestrielle': charges.filter(c => c.type === 'Trimestrielle' && !c.consultant),
    'Annuelle': charges.filter(c => c.type === 'Annuelle' && !c.consultant),
    'Mensuelle': charges.filter(c => c.type === 'Mensuelle' && !c.consultant),
    'Exceptionnelle': charges.filter(c => c.type === 'Exceptionnelle' && !c.consultant)
  };

  // Charges specifiques par consultant
  const chargesParConsultant = {};
  charges.filter(c => c.consultant).forEach(c => {
    const nom = consultantsMap[c.consultant] || c.consultant;
    if (!chargesParConsultant[nom]) chargesParConsultant[nom] = [];
    chargesParConsultant[nom].push(c);
  });
  const consultantNamesWithCharges = Object.keys(chargesParConsultant).sort();

  const getMoisLabel = (mois) => {
    const moisLabels = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return moisLabels[mois] || '';
  };

  // Regrouper les charges par nom et type
  const groupChargesByName = (typeCharges) => {
    const grouped = {};

    typeCharges.forEach(charge => {
      const key = `${charge.nom}`;
      if (!grouped[key]) {
        grouped[key] = {
          nom: charge.nom,
          type: charge.type,
          charges: [],
          totalMontant: 0
        };
      }
      grouped[key].charges.push(charge);
      grouped[key].totalMontant += charge.montant;
    });

    // Trier les charges de chaque groupe
    Object.values(grouped).forEach(group => {
      group.charges.sort((a, b) => {
        if (a.type === 'Trimestrielle') {
          const trimestreOrder = { 'Q1': 1, 'Q2': 2, 'Q3': 3, 'Q4': 4 };
          return (trimestreOrder[a.trimestre] || 0) - (trimestreOrder[b.trimestre] || 0);
        }
        return (a.mois || 0) - (b.mois || 0);
      });
    });

    return Object.values(grouped);
  };

  const toggleRow = (key) => {
    setExpandedRows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '"Noto Sans", sans-serif'
    }}>
      <PageHeader
        icon={Receipt}
        title="Gestion des Charges"
        subtitle="Suivi et gestion des charges du GIE"
      />

      {/* Header avec filtre et boutons */}
      <FilterBar
        filters={[
          {
            type: 'select',
            value: selectedYear,
            onChange: setSelectedYear,
            options: availableYears.map(y => ({ value: y, label: y.toString() })),
            placeholder: '—',
            allowNull: false
          }
        ]}
        infoDisplay={
          <ConsultantConfigButton
            nombreConsultants={nombreConsultants}
            onClick={() => setShowConfigModal(true)}
            canEdit={canEditCharges}
          />
        }
        actionButtons={
          <>
            <button
              onClick={() => canEditCharges && setShowCopyModal(true)}
              disabled={!canEditCharges}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: colors.primary.teal,
                color: colors.text.white,
                border: 'none',
                borderRadius: '0.5rem',
                cursor: !canEditCharges ? 'not-allowed' : 'pointer',
                fontSize: '0.9375rem',
                fontFamily: '"Noto Sans", sans-serif',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 200ms ease-in-out',
                opacity: !canEditCharges ? 0.5 : 1
              }}
              onMouseEnter={(e) => canEditCharges && (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => canEditCharges && (e.currentTarget.style.opacity = '1')}
              title={!canEditCharges ? "Vous n'avez pas les permissions pour copier les charges" : "Copier les charges d'une année à l'autre"}
            >
              <Copy size={18} />
              Copier les charges
            </button>

            <button
              onClick={() => canEditCharges && handleOpenModal('create')}
              disabled={!canEditCharges}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: colors.primary.gold,
                color: colors.text.white,
                border: 'none',
                borderRadius: '0.5rem',
                cursor: !canEditCharges ? 'not-allowed' : 'pointer',
                fontSize: '0.9375rem',
                fontFamily: '"Noto Sans", sans-serif',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 200ms ease-in-out',
                opacity: !canEditCharges ? 0.5 : 1
              }}
              onMouseEnter={(e) => canEditCharges && (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => canEditCharges && (e.currentTarget.style.opacity = '1')}
              title={!canEditCharges ? "Vous n'avez pas les permissions pour créer une charge" : "Créer une nouvelle charge"}
            >
              <Plus size={18} />
              Nouvelle charge
            </button>
          </>
        }
        marginBottom="2rem"
      />

      {loading ? (
        <div style={{
          padding: '3rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: `4px solid ${colors.secondary.mediumGray}`,
            borderTop: `4px solid ${colors.primary.teal}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            marginTop: '1rem',
            fontSize: '1rem',
            color: colors.primary.gray
          }}>
            Chargement...
          </p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      ) : (
        <AnimatedContent>
          {/* Résumé par trimestre */}
          <ChargesSummaryCards
            nombreConsultants={nombreConsultants}
            charges={charges}
            consultantsMap={consultantsMap}
          />

          {/* Charges communes par type - Layout flexible */}
          <ChargesTablesGrid>
            {Object.entries(chargesByType).map(([type, typeCharges]) => {
              const groupedCharges = groupChargesByName(typeCharges);

              return (
                <ChargesTable
                  key={type}
                  type={type}
                  charges={typeCharges}
                  groupedCharges={groupedCharges}
                  nombreConsultants={nombreConsultants}
                  consultantsMap={consultantsMap}
                  expandedRows={expandedRows}
                  onToggleRow={toggleRow}
                  onEditGroup={handleEditGroup}
                  onDeleteGroup={handleDeleteGroup}
                  onEditCharge={(charge) => handleOpenModal('edit', charge)}
                  onDeleteCharge={handleDeleteClick}
                  getMoisLabel={getMoisLabel}
                  canEdit={canEditCharges}
                />
              );
            })}
          </ChargesTablesGrid>

          {/* Charges par consultant */}
          {consultantNamesWithCharges.length > 0 && (
            <ChargesTablesGrid>
              {consultantNamesWithCharges.map(name => {
                const consultantCharges = chargesParConsultant[name];
                const grouped = groupChargesByName(consultantCharges);
                return (
                  <ChargesConsultantTable
                    key={name}
                    consultantName={name}
                    charges={consultantCharges}
                    groupedCharges={grouped}
                    expandedRows={expandedRows}
                    onToggleRow={toggleRow}
                    onEditGroup={handleEditGroup}
                    onDeleteGroup={handleDeleteGroup}
                    onEditCharge={(charge) => handleOpenModal('edit', charge)}
                    onDeleteCharge={handleDeleteClick}
                    getMoisLabel={getMoisLabel}
                    canEdit={canEditCharges}
                  />
                );
              })}
            </ChargesTablesGrid>
          )}
        </AnimatedContent>
      )}

      {/* Modales */}
      <ChargeFormModal
        show={showModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitCharge}
        mode={modalMode}
        charge={selectedCharge}
        filterAnnee={selectedYear}
        consultants={consultantsList}
        existingCharges={charges}
      />

      <DeleteChargeModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        charge={chargeToDelete}
      />

      <CopyChargesModal
        show={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        onCopy={handleCopyCharges}
      />

      <ConsultantConfigModal
        show={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onSave={handleSaveConsultantConfig}
        selectedConsultantIds={selectedConsultantIds}
        consultantsList={consultantsList}
        annee={selectedYear}
      />
    </div>
  );
}

export default ChargesPage;
